/**
 * What happens to a split's cut sheet when its customer changes (PRO-784).
 *
 * Before the marketplace, a cut sheet belonged to the person who filled it in,
 * so changing a split's customer took it off and the split went back to "cut
 * sheet needed". The exception is a cut sheet the animal has already been cut
 * to: the processor locked it, or the job has reached Invoicing. Then it is
 * the record of how the meat was cut, so it stays and only the customer
 * changes.
 *
 * One definition for the server (which clears it) and the confirm dialog
 * (which says whether it will be cleared).
 */

/** Job/animal statuses at which the meat has been cut. */
export const CUT_COMPLETE_STATUSES: readonly string[] = [
  'Invoicing',
  'Ready, Pay At Pickup',
  'Invoice Paid',
  'Completed',
];

/** Why a split's cut sheet stays when the customer changes. */
export type CutsheetKeepReason = 'locked' | 'cut';

/**
 * Why a split's cut sheet stays on it when the customer changes, or null when
 * it comes off.
 * @param split - The split being changed.
 * @param split.locked - The processor locked the cut sheet.
 * @param status - The animal's (or its job's) status.
 * @returns 'locked', 'cut' (the meat has been cut to it), or null.
 */
export function cutsheetKeepReason(
  split: { locked?: boolean | null },
  status?: string | null,
): CutsheetKeepReason | null {
  if (split.locked) return 'locked';
  if (status != null && CUT_COMPLETE_STATUSES.includes(status)) return 'cut';
  return null;
}

/**
 * Whether a split's cut sheet stays on it when the customer changes.
 * @param split - The split being changed.
 * @param split.locked - The processor locked the cut sheet.
 * @param status - The animal's (or its job's) status.
 * @returns True when the cut sheet is kept.
 */
export function keepsCutsheetOnCustomerChange(
  split: { locked?: boolean | null },
  status?: string | null,
): boolean {
  return cutsheetKeepReason(split, status) != null;
}
